import React, { useState, useEffect, useRef } from 'react';
import { getMessages, sendMessage, subscribeToMessages, unsubscribeFromMessages } from '../../../services/chatService';
import Icon from '../../../components/AppIcon';

export default function TechChatWindow({ conversation, currentUser, currentName, currentRole }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const channelRef = useRef(null);

  useEffect(() => {
    if (!conversation?.id) return;
    setLoading(true);
    getMessages(conversation?.id)?.then((msgs) => {
      setMessages(msgs);
      setLoading(false);
    });

    channelRef.current = subscribeToMessages(conversation?.id, (newMsg) => {
      setMessages((prev) => {
        if (prev?.find((m) => m?.id === newMsg?.id)) return prev;
        return [...prev, newMsg];
      });
    });

    return () => {
      if (channelRef?.current) unsubscribeFromMessages(channelRef?.current);
    };
  }, [conversation?.id]);

  useEffect(() => {
    bottomRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!text?.trim() || sending) return;
    setSending(true);
    try {
      await sendMessage(conversation?.id, currentUser, currentName, currentRole, text?.trim());
      setText('');
    } catch (err) {
      console.error('Send error:', err);
    } finally {
      setSending(false);
    }
  };

  const peerName = currentRole === 'doctor'
    ? conversation?.tech_name
    : conversation?.doctor_name;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-card">
        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon name="User" size={18} className="text-primary" />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">{peerName || 'Unknown'}</p>
          <p className="text-xs text-muted-foreground">
            {currentRole === 'doctor' ? 'Diagnostic Technologist' : 'Referring Doctor'}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-xs text-muted-foreground">Live</span>
        </div>
      </div>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-muted/20">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          </div>
        ) : messages?.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="MessageCircle" size={22} className="text-primary" />
            </div>
            <p className="text-sm text-muted-foreground">No messages yet. Start the conversation.</p>
          </div>
        ) : (
          messages?.map((msg) => {
            const isMine = msg?.sender_id === currentUser;
            return (
              <div key={msg?.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed shadow-sm ${
                  isMine
                    ? 'bg-primary text-primary-foreground rounded-br-sm'
                    : 'bg-card text-foreground border border-border rounded-bl-sm'
                }`}>
                  {!isMine && (
                    <p className="text-xs font-medium mb-1 opacity-70">{msg?.sender_name}</p>
                  )}
                  <p>{msg?.content}</p>
                  <p className={`text-xs mt-1 ${isMine ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>
                    {new Date(msg.created_at)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>
      {/* Input */}
      <form onSubmit={handleSend} className="flex items-center gap-2 px-4 py-3 border-t border-border bg-card">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e?.target?.value)}
          placeholder="Type a message…"
          className="flex-1 px-4 py-2 rounded-full border border-border bg-background text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
        <button
          type="submit"
          disabled={!text?.trim() || sending}
          className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors disabled:opacity-50 flex-shrink-0"
        >
          {sending
            ? <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
            : <Icon name="Send" size={16} />
          }
        </button>
      </form>
    </div>
  );
}
